import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import { useGetMeetingsByDepartmentQuery } from '@shared/api';
import { useAgentPermission } from '@entities/user';
import { fmtMeetingDate, meetingStatusLabel, meetingStatusTone } from '@entities/meetings';
import { Card } from '@shared/ui/Card';
import { FallbackBanner } from '@shared/ui/FallbackBanner';

const BackLink = styled(Link)`
  display: inline-block;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing(4)};

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const DeptTitle = styled.h2`
  margin: 0 0 ${({ theme }) => theme.spacing(5)};
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const MeetingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const MeetingRow = styled(Card).attrs({ as: Link })`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
  text-decoration: none;
  color: inherit;
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderStrong};
    box-shadow: ${({ theme }) => theme.shadow.lg};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const MeetingMain = styled.div`
  min-width: 0;
`;

const MeetingTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const MeetingMeta = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: ${({ theme }) => theme.spacing(0.5)};
`;

const StatusTag = styled.span<{ $tone: 'success' | 'danger' | 'neutral' }>`
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  padding: ${({ theme }) => `${theme.spacing(0.5)} ${theme.spacing(2)}`};
  border-radius: ${({ theme }) => theme.radius.pill};
  color: ${({ theme, $tone }) =>
    $tone === 'neutral' ? theme.colors.text.muted : theme.colors.status[$tone]};
  background: ${({ theme, $tone }) =>
    $tone === 'neutral' ? theme.colors.surfaceHover : `${theme.colors.status[$tone]}18`};
`;

const EmptyHint = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/**
 * Страница отдела (блок E): список совещаний одного отдела (отдел = папка/серия).
 * Имя отдела берём из совещаний (department_name денормализован); открывается из
 * папок «Отделы» на обзоре. Совещание → детальная страница.
 */
export function MeetingDepartment() {
  const { departmentId = '' } = useParams();
  const { can_read } = useAgentPermission('meetings');
  const { data } = useGetMeetingsByDepartmentQuery(departmentId, { skip: !departmentId });

  if (!can_read) {
    return (
      <FallbackBanner tone="danger" title="Доступ запрещён">
        У вас нет прав на раздел «Совещания».
      </FallbackBanner>
    );
  }

  const meetings = data?.meetings ?? [];
  const deptName = meetings[0]?.department_name ?? 'Отдел';

  return (
    <div>
      <BackLink to="/agents/meetings">← К обзору</BackLink>
      <DeptTitle>Отдел: {deptName}</DeptTitle>
      {meetings.length > 0 ? (
        <MeetingList>
          {meetings.map((m) => (
            <MeetingRow to={`/agents/meetings/meetings/${m.id}`} key={m.id}>
              <MeetingMain>
                <MeetingTitle>{m.title}</MeetingTitle>
                <MeetingMeta>{fmtMeetingDate(m.created_at)}</MeetingMeta>
              </MeetingMain>
              <StatusTag $tone={meetingStatusTone(m.status)}>
                {meetingStatusLabel(m.status)}
              </StatusTag>
            </MeetingRow>
          ))}
        </MeetingList>
      ) : (
        <EmptyHint>В этом отделе пока нет совещаний.</EmptyHint>
      )}
    </div>
  );
}
